import { useState, useEffect } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import {
  Lock, Users, Calendar, Check, ArrowRight, Sparkles, Landmark,
  MessagesSquare, Handshake, Coffee, Globe, ListChecks, Wine, Trophy,
  Award, Leaf, Star, MapPin, CheckCircle2, ShieldCheck,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useLanguage } from "@/i18n/LanguageContext";
import { Reveal } from "@/components/Reveal";
import { useFormDialogs } from "@/components/FormDialogs";
import { AddToCalendar } from "@/components/AddToCalendar";
import { ProgramTopics } from "@/components/ProgramTopics";
import { speakerInitials, speakerSrc, speakerSrcSet } from "@/lib/speakerImg";
import { usePageMeta } from "@/lib/pageMeta";

const Roundtable = ({ r, speakers }) => {
  const { openTicket } = useFormDialogs();
  const [selected, setSelected] = useState(null);
  const speakerProfiles = speakers?.list || [];
  return (
    <div className="space-y-14">
      <Reveal>
        <div className="inline-flex items-center gap-2 border border-[#CEA26B]/40 bg-[#CEA26B]/10 px-4 py-2">
          <Lock className="h-4 w-4 text-[#CEA26B]" />
          <span className="text-sm font-semibold text-[#CEA26B] tracking-wide">{r.badge}</span>
        </div>
      </Reveal>

      <div className="space-y-5 max-w-3xl">
        {r.intro.map((p, i) => (
          <Reveal key={i} delay={0.05 * i}>
            <p
              data-testid={`roundtable-intro-${i}`}
              className={`leading-relaxed ${i === 0 ? "text-lg md:text-xl text-white font-display" : "text-base md:text-lg text-slate-300"}`}
            >
              {p}
            </p>
          </Reveal>
        ))}
      </div>

      {r.speakersList?.length > 0 && (
        <div>
          <Reveal>
            <h3 className="font-display font-bold text-white text-2xl sm:text-3xl tracking-tight" data-testid="roundtable-speakers-title">
              {r.speakersTitle}
            </h3>
          </Reveal>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...r.speakersList]
              .sort((a, b) => {
                const la = a.name.trim().split(" ").pop();
                const lb = b.name.trim().split(" ").pop();
                return la.localeCompare(lb, "cs");
              })
              .map((sp, i) => {
                const profile = speakerProfiles.find((item) => item.name === sp.name);
                const medallion = profile ? { ...sp, ...profile } : sp;
                const hasMedallion = medallion.medallion !== false;
                const Card = hasMedallion ? "button" : "div";
                return (
                <Reveal key={sp.name} delay={0.05 * i}>
                  <Card
                    type={hasMedallion ? "button" : undefined}
                    onClick={hasMedallion ? () => setSelected(medallion) : undefined}
                    data-testid={`roundtable-speaker-card-${i}`}
                    aria-label={hasMedallion ? `${speakers?.bioCta || "Profil"}: ${sp.name}` : undefined}
                    className={`group h-full w-full max-w-[280px] mx-auto sm:max-w-none text-left border border-white/10 bg-white/[0.02] overflow-hidden transition-[transform,border-color,box-shadow] duration-300 ${hasMedallion ? "hover:border-[#CEA26B] hover:-translate-y-1 focus:outline-none focus:ring-2 focus:ring-[#CEA26B]" : ""}`}
                  >
                    <div className="aspect-[4/5] overflow-hidden bg-[#01154F]">
                      <img
                        src={speakerSrc(sp.photo, 640)}
                        srcSet={speakerSrcSet(sp.photo)}
                        sizes="(max-width: 640px) 280px, (max-width: 1024px) 45vw, 22vw"
                        alt={sp.name}
                        loading="lazy"
                        className="w-full h-full object-cover object-top grayscale-[20%] group-hover:grayscale-0 group-hover:scale-[1.03] transition-all duration-500"
                      />
                    </div>
                    <div className="px-5 py-5">
                      <h4 className="font-display font-bold text-white text-lg leading-snug">{sp.name}</h4>
                      <p className="mt-1 text-sm text-[#CEA26B] font-medium">{sp.role}</p>
                      <p className="text-sm text-slate-400">{sp.org}</p>
                      {hasMedallion && (
                        <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold tracking-wide uppercase text-[#CEA26B] group-hover:gap-2.5 transition-[gap] duration-300">
                          {speakers?.bioCta}
                          <ArrowRight className="h-3.5 w-3.5" />
                        </span>
                      )}
                    </div>
                  </Card>
                </Reveal>
              );
              })}
          </div>
          {r.speakersNote && (
            <p className="mt-6 text-base text-slate-400 italic" data-testid="roundtable-speakers-note">{r.speakersNote}</p>
          )}
        </div>
      )}

      <div className="max-w-3xl">
        <Reveal>
          <h3 className="font-display font-bold text-white text-2xl sm:text-3xl tracking-tight">{r.whyTitle}</h3>
        </Reveal>
        <div className="mt-6 space-y-5">
          {r.whyParagraphs.map((p, i) => (
            <Reveal key={i} delay={0.05 * i}>
              <p className="text-base md:text-lg text-slate-300 leading-relaxed">{p}</p>
            </Reveal>
          ))}
        </div>
      </div>

      <div className="grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-6">
          <Reveal>
            <div className="flex items-center gap-3">
              <Users className="h-6 w-6 text-[#CEA26B]" />
              <h3 className="font-display font-bold text-white text-2xl tracking-tight">{r.forWhomTitle}</h3>
            </div>
            <p className="mt-5 text-base text-slate-400 leading-relaxed">{r.forWhomIntro}</p>
            <ul className="mt-6 space-y-px bg-white/10 border border-white/10">
              {r.forWhomItems.map((item, i) => (
                <li
                  key={i}
                  data-testid={`roundtable-forwhom-${i}`}
                  className="flex items-start gap-3 bg-[#010E3A] px-5 py-4 text-base text-slate-200"
                >
                  <Check className="h-5 w-5 text-[#CEA26B] shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>

        <div className="lg:col-span-6">
          <Reveal delay={0.08}>
            <div className="flex items-center gap-3">
              <Lock className="h-6 w-6 text-[#CEA26B]" />
              <h3 className="font-display font-bold text-white text-2xl tracking-tight">{r.principlesTitle}</h3>
            </div>
            <div className="mt-6 grid sm:grid-cols-2 gap-px bg-white/10 border border-white/10">
              {r.principles.map((p, i) => (
                <div
                  key={i}
                  data-testid={`roundtable-principle-${i}`}
                  className="bg-[#010E3A] px-5 py-4 text-sm text-slate-300 hover:bg-white/[0.03] transition-colors"
                >
                  {p}
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </div>

      {r.confidentiality && (
        <Reveal delay={0.06}>
          <div
            data-testid="roundtable-confidentiality"
            className="mt-14 border-l-2 border-[#CEA26B] bg-white/[0.02] px-7 py-7"
          >
            <div className="flex items-center gap-3">
              <ShieldCheck className="h-6 w-6 text-[#CEA26B]" />
              <h3 className="font-display font-bold text-white text-2xl tracking-tight">
                {r.confidentialityTitle}
              </h3>
            </div>
            <div className="mt-5 space-y-4">
              {r.confidentiality.map((p, i) => (
                <p key={i} data-testid={`roundtable-confidentiality-${i}`} className="text-base text-slate-300 leading-relaxed">
                  {p}
                </p>
              ))}
            </div>
          </div>
        </Reveal>
      )}

      <Reveal delay={0.1}>
        <div className="border border-[#CEA26B]/30 bg-[#CEA26B]/[0.06] p-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
          <p className="text-base md:text-lg text-white font-display max-w-2xl">{r.tagline}</p>
          <button
            onClick={() => openTicket()}
            data-testid="roundtable-cta-btn"
            className="group inline-flex items-center justify-center gap-2 bg-[#CEA26B] hover:bg-[#BE965D] text-[#01154F] px-7 py-3.5 font-semibold tracking-wide transition-colors whitespace-nowrap"
          >
            {r.cta}
            <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </Reveal>
      <SpeakerBioDialog selected={selected} onClose={() => setSelected(null)} />
    </div>
  );
};

const SpeakerBioDialog = ({ selected, onClose }) => (
  <Dialog open={!!selected} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="bg-[#0A2060] border-white/10 text-white sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="speaker-bio-dialog">
      {selected && (
        <div className="flex flex-col sm:flex-row gap-6">
          <div className="shrink-0 w-36 sm:w-44">
            <div className="aspect-[4/5] overflow-hidden bg-[#01154F] border border-[#CEA26B]/30">
              {selected.photo ? (
                <img src={speakerSrc(selected.photo, 640)} alt={selected.name} className="w-full h-full object-cover object-top" data-testid="speaker-bio-photo" />
              ) : (
                <div className="flex h-full w-full items-center justify-center font-display text-5xl font-bold text-[#CEA26B]" data-testid="speaker-bio-photo-placeholder">
                  {speakerInitials(selected.name)}
                </div>
              )}
            </div>
          </div>
          <div className="flex-1">
            <DialogHeader className="text-left space-y-0">
              <DialogTitle className="font-display text-2xl" data-testid="speaker-bio-name">{selected.name}</DialogTitle>
              <DialogDescription asChild>
                <div>
                  <p className="mt-1 text-sm text-[#CEA26B] font-medium">{selected.role}</p>
                  <p className="text-sm text-slate-400">{selected.org}</p>
                </div>
              </DialogDescription>
            </DialogHeader>
            <div className="mt-4 h-px w-16 bg-[#CEA26B]" />
            {selected.bio && (
              <p className="mt-4 text-sm text-slate-300 leading-relaxed" data-testid="speaker-bio-text">{selected.bio}</p>
            )}
          </div>
        </div>
      )}
    </DialogContent>
  </Dialog>
);

const scheduleIcons = [Lock, Users, Sparkles, Landmark, MessagesSquare, Handshake, Coffee, MessagesSquare, Handshake, MessagesSquare, Globe, MessagesSquare, ListChecks, Wine, Trophy, Handshake];
const breakItems = new Set([1, 6, 13]);
const phaseStarts = { 0: 0, 1: 1, 2: 2, 13: 3 };
const phaseTimes = ["11:30–13:00", "12:30–13:30", "13:30–17:20", "17:20–22:00"];
const keyItems = new Set([4, 7, 9, 10, 11]);

const Summit = ({ p }) => {
  const [selected, setSelected] = useState(null);
  return (
  <div>
    <Reveal>
      <div className="flex flex-wrap items-center gap-4">
        <div className="inline-flex items-center gap-2 border border-[#CEA26B]/30 bg-[#CEA26B]/10 px-4 py-2" data-testid="program-date">
          <Calendar className="h-4 w-4 text-[#CEA26B]" />
          <span className="font-display font-semibold text-white text-sm tracking-wide">{p.date}</span>
        </div>
        <div className="inline-flex items-center gap-2 border border-[#CEA26B]/30 bg-[#CEA26B]/10 px-4 py-2" data-testid="program-venue">
          <MapPin className="h-4 w-4 text-[#CEA26B]" />
          <span className="font-display font-semibold text-white text-sm tracking-wide">{p.venueLabel}</span>
        </div>
        <AddToCalendar />
      </div>
    </Reveal>
    {p.upcomingNote && (
      <Reveal delay={0.05}>
        <p className="mt-6 text-base md:text-lg text-slate-300 leading-relaxed max-w-3xl border-l-2 border-[#CEA26B] pl-5" data-testid="program-upcoming-note">
          {p.upcomingNote}
        </p>
      </Reveal>
    )}

    {p.scheduleTitle && (
      <Reveal>
        <h3 className="mt-14 font-display font-bold text-white text-2xl sm:text-3xl tracking-tight">{p.scheduleTitle}</h3>
        {p.host && (
          <div
            className="mt-5 inline-flex items-center gap-3 border border-[#CEA26B]/40 bg-[#CEA26B]/10 py-1.5 pl-1.5 pr-5"
            data-testid="program-host"
          >
            <img
              src={speakerSrc(p.host.photo, 160)}
              alt={p.host.name}
              className="h-12 w-12 object-cover object-top border border-[#CEA26B]/40"
            />
            <span className="text-sm text-white font-semibold">{p.host.name}</span>
            <span className="text-sm text-[#CEA26B]">· {p.host.label}</span>
          </div>
        )}
        {p.hostNote && (
          <p className="mt-3 text-sm text-slate-300 max-w-2xl" data-testid="program-host-note">{p.hostNote}</p>
        )}
      </Reveal>
    )}

    <div className="mt-10 relative">
      <span
        aria-hidden="true"
        className="absolute left-[23px] top-6 bottom-6 w-px bg-gradient-to-b from-[#CEA26B]/70 via-white/15 to-[#CEA26B]/70"
      />
      <div className="space-y-3">
        {p.items.map((item, i) => {
          const Icon = scheduleIcons[i] || Calendar;
          const isGala = item.time.startsWith("18:00");
          const isBreak = breakItems.has(i);
          const isKey = keyItems.has(i);
          const phaseIdx = phaseStarts[i];
          return (
            <div key={i}>
              {p.phases && phaseIdx !== undefined && (
                <Reveal>
                  <div className={`relative pl-16 sm:pl-20 pb-5 ${i === 0 ? "" : "pt-12"}`} data-testid={`program-phase-${phaseIdx}`}>
                    <span className={`absolute left-[18px] h-3 w-3 rotate-45 bg-[#CEA26B] shadow-[0_0_16px_rgba(206,162,107,0.95)] ${i === 0 ? "top-[22px]" : "top-[70px]"}`} />
                    <div className="flex items-end gap-4 flex-wrap">
                      <span
                        aria-hidden="true"
                        className="font-display font-black text-5xl md:text-6xl leading-none text-transparent select-none"
                        style={{ WebkitTextStroke: "1.5px rgba(206,162,107,0.55)" }}
                      >
                        {String(phaseIdx + 1).padStart(2, "0")}
                      </span>
                      <div className="pb-0.5">
                        <span className="overline text-[#CEA26B] block">{p.phases[phaseIdx]}</span>
                        <span className="mt-1 block text-xs text-slate-500 tabular-nums tracking-[0.25em]">{phaseTimes[phaseIdx]}</span>
                      </div>
                      <span className="h-px flex-1 bg-gradient-to-r from-[#CEA26B]/40 to-transparent mb-3 min-w-[60px]" />
                    </div>
                  </div>
                </Reveal>
              )}
              <Reveal delay={0.02 * i}>
                <div data-testid={`program-item-${i}`} className="group relative pl-16 sm:pl-20">
                  <span
                    className={`absolute left-0 top-4 flex h-12 w-12 items-center justify-center border transition-colors duration-300 ${
                      isGala
                        ? "bg-[#CEA26B] border-[#CEA26B] shadow-[0_0_24px_rgba(206,162,107,0.55)]"
                        : isBreak
                        ? "bg-[#010E3A] border-white/15 group-hover:border-[#CEA26B]/50"
                        : "bg-[#010E3A] border-[#CEA26B]/40 group-hover:bg-[#CEA26B] group-hover:border-[#CEA26B] group-hover:shadow-[0_0_18px_rgba(206,162,107,0.45)]"
                    }`}
                  >
                    <Icon
                      className={`h-5 w-5 transition-[color,transform] duration-300 group-hover:scale-110 ${
                        isGala
                          ? "text-[#01154F]"
                          : isBreak
                          ? "text-slate-400 group-hover:text-[#CEA26B]"
                          : "text-[#CEA26B] group-hover:text-[#01154F]"
                      }`}
                    />
                  </span>

                  <div
                    className={`relative overflow-hidden p-5 sm:p-6 transition-[transform,border-color,background-color] duration-300 group-hover:-translate-y-0.5 ${
                      isGala
                        ? "border border-[#CEA26B]/60 bg-gradient-to-r from-[#CEA26B]/20 via-[#CEA26B]/10 to-transparent hover:border-[#CEA26B]"
                        : isBreak
                        ? "border border-dashed border-white/15 bg-transparent hover:border-white/30"
                        : isKey
                        ? "border border-[#CEA26B]/25 bg-[#CEA26B]/[0.05] hover:border-[#CEA26B]/60 hover:bg-[#CEA26B]/[0.08]"
                        : "border border-white/10 bg-white/[0.02] hover:border-[#CEA26B]/50 hover:bg-white/[0.04]"
                    }`}
                  >
                    {isKey && (
                      <>
                        <span aria-hidden="true" className="pointer-events-none absolute left-0 top-0 h-5 w-5 border-l-2 border-t-2 border-[#CEA26B]" />
                        <span aria-hidden="true" className="pointer-events-none absolute right-0 bottom-0 h-5 w-5 border-r-2 border-b-2 border-[#CEA26B]" />
                      </>
                    )}
                    {isGala && (
                      <span aria-hidden="true" className="pointer-events-none absolute inset-0 gala-sheen" />
                    )}
                    <span
                      aria-hidden="true"
                      className={`pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 font-display font-black tabular-nums leading-none select-none transition-colors duration-300 ${
                        isGala
                          ? "text-[#CEA26B]/15 text-7xl md:text-8xl"
                          : "text-white/[0.05] group-hover:text-[#CEA26B]/10 text-6xl md:text-7xl"
                      }`}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    {isGala && (
                      <Trophy aria-hidden="true" className="pointer-events-none absolute right-4 bottom-4 h-32 w-32 text-[#CEA26B]/10 rotate-12" />
                    )}

                    <div className="relative flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-8">
                      <span
                        className={`inline-flex self-start shrink-0 items-center px-3.5 py-1.5 font-display font-bold tabular-nums text-sm tracking-wide ${
                          isGala
                            ? "bg-[#CEA26B] text-[#01154F] shadow-[0_0_16px_rgba(206,162,107,0.4)]"
                            : "border border-[#CEA26B]/30 bg-[#CEA26B]/10 text-[#CEA26B] group-hover:border-[#CEA26B]/60 transition-colors duration-300"
                        }`}
                      >
                        {item.time}
                      </span>
                      <div className={`min-w-0 flex-1 ${item.topics ? "" : "sm:pr-16"}`}>
                        {item.badge && (
                          <span
                            data-testid={`program-item-badge-${i}`}
                            className="mb-2 flex w-fit items-center gap-1.5 bg-[#CEA26B] text-[#01154F] px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.15em] shadow-[0_0_16px_rgba(206,162,107,0.45)]"
                          >
                            <Star className="h-3 w-3 fill-current" aria-hidden="true" />
                            {item.badge}
                          </span>
                        )}
                        <span
                          className={`${
                            isGala
                              ? "font-display font-bold text-white text-lg md:text-xl"
                              : isBreak
                              ? "text-slate-300 text-base md:text-lg font-medium"
                              : "text-white text-base md:text-lg font-semibold"
                          }`}
                        >
                          {item.title}
                        </span>
                        {item.desc && (
                          <p className={`mt-2 text-sm leading-relaxed max-w-2xl ${isGala ? "text-slate-200" : "text-slate-400"}`}>
                            {item.desc}
                          </p>
                        )}
                        {item.paragraphs && item.paragraphs.map((pg, pi) => (
                          <p key={pi} className="mt-3 text-sm leading-relaxed max-w-2xl text-slate-400">{pg}</p>
                        ))}
                        {item.topics && (
                          <ProgramTopics topics={item.topics} patronLabel={p.topicPatronLabel} itemIndex={i} />
                        )}
                        {item.footerNotes && item.footerNotes.map((fn, fi) => (
                          <p key={fi} className={`mt-3 text-sm leading-relaxed max-w-2xl ${fi === 0 ? "text-[#CEA26B]/90 font-medium" : "text-slate-400 italic"}`}>{fn}</p>
                        ))}
                        {item.note && (
                          <p className="mt-3 text-sm leading-relaxed max-w-2xl text-[#CEA26B]/90" data-testid={`program-item-note-${i}`}>
                            {item.note}{" "}
                            <a
                              href={item.noteUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="font-semibold underline underline-offset-2 hover:text-white transition-colors"
                            >
                              {item.noteLink}
                            </a>
                          </p>
                        )}
                        {isGala && p.gala?.hosts && (
                          <div className="mt-4 flex flex-wrap gap-2" data-testid="program-gala-moderator">
                            {p.gala.hosts.map((h) => (
                              <span key={h.name} className="inline-flex items-center gap-2.5 border border-[#CEA26B]/40 bg-[#CEA26B]/10 py-1 pl-1 pr-3.5 text-sm text-slate-100">
                                <img
                                  src={speakerSrc(h.photo, 160)}
                                  alt={h.name}
                                  className="h-16 w-16 object-cover object-top border border-[#CEA26B]/40"
                                />
                                {h.name}
                                <span className="text-[#CEA26B]">· {h.label}</span>
                              </span>
                            ))}
                          </div>
                        )}
                        {(item.speakers || item.moderator) && (
                          <div className="mt-4 flex flex-wrap gap-2" data-testid={`program-item-speakers-${i}`}>
                            {item.moderator && (() => {
                              const msp = (p.speakers?.list || []).find((x) => x.name === item.moderator);
                              return (
                                <button
                                  type="button"
                                  onClick={() => msp && setSelected(msp)}
                                  data-testid={`program-moderator-chip-${i}-${item.moderator.replace(/\s+/g, "-").toLowerCase()}`}
                                  className={`inline-flex items-center gap-2.5 border border-[#CEA26B]/40 bg-[#CEA26B]/10 py-1 pr-3.5 text-sm text-slate-100 hover:border-[#CEA26B] hover:text-white transition-colors duration-300 ${
                                    msp ? "pl-1 cursor-pointer" : "pl-3.5 pointer-events-none"
                                  }`}
                                >
                                  {msp ? (
                                    <img src={speakerSrc(msp.photo, 160)} alt={item.moderator} className="h-16 w-16 object-cover object-top border border-[#CEA26B]/40" />
                                  ) : (
                                    <span className="h-5 w-1 bg-[#CEA26B]" />
                                  )}
                                  {item.moderator}
                                  <span className="text-[#CEA26B]">· {item.moderatorLabel || p.moderatorLabel}</span>
                                </button>
                              );
                            })()}
                            {[...(item.speakers || [])]
                              .map((nm) => ({ nm, sp: (p.speakers?.list || []).find((x) => x.name === nm) }))
                              .sort((a, b) => {
                                if (item.preserveSpeakerOrder) return 0;
                                if (!a.sp || !b.sp) return !a.sp ? (!b.sp ? 0 : 1) : -1;
                                return a.nm.trim().split(" ").pop().localeCompare(b.nm.trim().split(" ").pop(), "cs");
                              })
                              .map(({ nm, sp }) => (
                                <button
                                  type="button"
                                  key={nm}
                                  onClick={() => sp && setSelected(sp)}
                                  data-testid={`program-speaker-chip-${i}-${nm.replace(/\s+/g, "-").toLowerCase()}`}
                                  className={`inline-flex items-center gap-2.5 border border-white/15 bg-white/[0.04] py-1 pr-3.5 text-sm text-slate-100 hover:border-[#CEA26B] hover:text-white transition-colors duration-300 ${
                                    sp ? "pl-1 cursor-pointer" : "pl-3.5 pointer-events-none"
                                  }`}
                                >
                                  {sp?.photo ? (
                                    <img src={speakerSrc(sp.photo, 160)} alt={nm} className="h-16 w-16 object-cover object-top border border-[#CEA26B]/40" />
                                  ) : sp ? (
                                    <span className="flex h-16 w-16 items-center justify-center border border-[#CEA26B]/40 bg-[#0A2060] font-display text-sm font-bold text-[#CEA26B]">
                                      {speakerInitials(nm)}
                                    </span>
                                  ) : (
                                    <span className="h-5 w-1 bg-[#CEA26B]" />
                                  )}
                                  {nm}
                                </button>
                              ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </Reveal>
            </div>
          );
        })}
      </div>
    </div>
    <SpeakerBioDialog selected={selected} onClose={() => setSelected(null)} />
  </div>
  );
};

const Speakers = ({ s }) => {
  const [selected, setSelected] = useState(null);
  const [params, setParams] = useSearchParams();
  const spParam = params.get("sp");

  useEffect(() => {
    if (spParam) {
      const found = (s.list || []).find((x) => x.name === spParam);
      if (found && found.bio) setSelected(found);
    }
  }, [spParam, s.list]);

  const closeDialog = () => {
    setSelected(null);
    if (params.get("sp")) {
      params.delete("sp");
      setParams(params, { replace: true });
    }
  };

  const sorted = [...(s.list || [])].sort((a, b) => {
    const sa = a.name.trim().split(" ").pop();
    const sb = b.name.trim().split(" ").pop();
    return sa.localeCompare(sb, "cs");
  });
  return (
    <div>
      <Reveal>
        <p className="text-base text-slate-400 leading-relaxed max-w-2xl">{s.subtitle}</p>
      </Reveal>
      {sorted.length > 0 && (
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {sorted.map((sp, i) => (
            <Reveal key={i} delay={0.05 * i}>
              <div
                data-testid={`speaker-card-${i}`}
                onClick={() => sp.bio && setSelected(sp)}
                role={sp.bio ? "button" : undefined}
                tabIndex={sp.bio ? 0 : undefined}
                onKeyDown={(e) => sp.bio && e.key === "Enter" && setSelected(sp)}
                className={`group h-full w-full max-w-[280px] mx-auto sm:max-w-none border border-white/10 bg-white/[0.02] overflow-hidden hover:border-[#CEA26B]/40 transition-all duration-300 ${
                  sp.bio ? "cursor-pointer" : ""
                }`}
              >
                <div className="relative aspect-[4/5] overflow-hidden bg-[#01154F]">
                  {sp.badge && (
                    <span
                      data-testid={`speaker-card-badge-${i}`}
                      className="absolute left-0 top-3 z-10 inline-flex items-center gap-1.5 bg-[#CEA26B] text-[#01154F] px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.12em] shadow-[0_0_16px_rgba(206,162,107,0.5)]"
                    >
                      <Star className="h-3 w-3 fill-current" aria-hidden="true" />
                      {sp.badge}
                    </span>
                  )}
                  {sp.photo ? (
                    <img
                      src={speakerSrc(sp.photo, 640)}
                      srcSet={speakerSrcSet(sp.photo)}
                      sizes="(max-width: 640px) 280px, (max-width: 1024px) 45vw, 30vw"
                      alt={sp.name}
                      loading="lazy"
                      className="w-full h-full object-cover object-top grayscale-[20%] group-hover:grayscale-0 group-hover:scale-[1.03] transition-all duration-500"
                      data-testid={`speaker-card-photo-${i}`}
                    />
                  ) : (
                    <div
                      data-testid={`speaker-card-placeholder-${i}`}
                      className="flex h-full w-full items-center justify-center bg-[#0A2060] font-display text-6xl font-bold text-[#CEA26B]"
                      aria-label={sp.name}
                    >
                      {speakerInitials(sp.name)}
                    </div>
                  )}
                </div>
                <div className="p-6 border-t-2 border-[#CEA26B]">
                  <h4 className="font-display font-bold text-white text-lg">{sp.name}</h4>
                  <p className="mt-1 text-sm text-[#CEA26B] font-medium">{sp.role}</p>
                  <p className="text-sm text-slate-400">{sp.org}</p>
                  {sp.bio && (
                    <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold tracking-wide uppercase text-[#CEA26B] group-hover:gap-2.5 transition-[gap] duration-300">
                      {s.bioCta}
                      <ArrowRight className="h-3.5 w-3.5" />
                    </span>
                  )}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      )}
      {s.note && (
        <Reveal delay={0.1}>
          <p className="mt-10 text-sm text-slate-400 leading-relaxed max-w-3xl" data-testid="speakers-note">
            {s.note}
          </p>
        </Reveal>
      )}

      <SpeakerBioDialog selected={selected} onClose={closeDialog} />
    </div>
  );
};

const galaAwardIcons = [Landmark, Award, Leaf, Star];

const galaPhotos = [
  "/img/gala/gala-2025-4491.jpg",
  "/img/gala/gala-2025-4578.jpg",
  "/img/gala/gala-2025-4704.jpg",
  "/img/gala/gala-2025-4632.jpg",
  "/img/gala/gala-2025-4649.jpg",
  "/img/gala/gala-2025-4612.jpg",
  "/img/gala/gala-2025-4672.jpg",
  "/img/gala/gala-2025-4746.jpg",
];

const GalaGallery = ({ gal }) => {
  const [lightbox, setLightbox] = useState(null);
  const move = (d) => setLightbox((i) => (i + d + galaPhotos.length) % galaPhotos.length);

  useEffect(() => {
    if (lightbox === null) return;
    const onKey = (e) => {
      if (e.key === "ArrowRight") move(1);
      if (e.key === "ArrowLeft") move(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox]);

  return (
    <div data-testid="gala-gallery">
      <Reveal>
        <h3 className="font-display font-bold text-white text-2xl sm:text-3xl tracking-tight">{gal.title}</h3>
        <p className="mt-4 text-base text-slate-400 leading-relaxed max-w-3xl">{gal.text}</p>
      </Reveal>
      <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-3">
        {galaPhotos.map((src, i) => (
          <Reveal key={src} delay={0.05 * i}>
            <button
              type="button"
              onClick={() => setLightbox(i)}
              data-testid={`gala-gallery-photo-${i}`}
              className="group block w-full aspect-[3/2] overflow-hidden border border-white/10 hover:border-[#CEA26B]/60 transition-colors duration-300 cursor-pointer"
            >
              <img
                src={src}
                alt={`${gal.title} ${i + 1}`}
                loading="lazy"
                className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500"
              />
            </button>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.1}>
        <a
          href={gal.url}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="gala-gallery-link"
          className="mt-6 inline-flex items-center gap-2 text-sm font-semibold tracking-wide uppercase text-[#CEA26B] hover:text-white transition-colors"
        >
          {gal.linkLabel}
          <ArrowRight className="h-4 w-4" />
        </a>
      </Reveal>

      <Dialog open={lightbox !== null} onOpenChange={(o) => !o && setLightbox(null)}>
        <DialogContent
          className="bg-[#010E3A] border-white/10 text-white p-2 sm:max-w-4xl"
          data-testid="gala-gallery-lightbox"
        >
          {lightbox !== null && (
            <div className="relative">
              <DialogHeader className="sr-only">
                <DialogTitle>{gal.title}</DialogTitle>
                <DialogDescription>{`${lightbox + 1} / ${galaPhotos.length}`}</DialogDescription>
              </DialogHeader>
              <img src={galaPhotos[lightbox]} alt={`${gal.title} ${lightbox + 1}`} className="w-full h-auto" />
              <button
                type="button"
                onClick={() => move(-1)}
                data-testid="gala-gallery-prev"
                aria-label="Previous"
                className="absolute left-2 top-1/2 -translate-y-1/2 h-10 w-10 flex items-center justify-center bg-[#01154F]/80 border border-white/20 text-white hover:border-[#CEA26B] transition-colors"
              >
                <ArrowRight className="h-5 w-5 rotate-180" />
              </button>
              <button
                type="button"
                onClick={() => move(1)}
                data-testid="gala-gallery-next"
                aria-label="Next"
                className="absolute right-2 top-1/2 -translate-y-1/2 h-10 w-10 flex items-center justify-center bg-[#01154F]/80 border border-white/20 text-white hover:border-[#CEA26B] transition-colors"
              >
                <ArrowRight className="h-5 w-5" />
              </button>
              <span className="absolute bottom-2 right-3 text-xs text-slate-300 bg-[#01154F]/80 px-2 py-1">
                {lightbox + 1} / {galaPhotos.length}
              </span>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Gala = ({ g }) => (
  <div className="space-y-12">
    <Reveal>
      <div className="flex flex-wrap items-center gap-3">
        <div className="inline-flex items-center gap-2 border border-[#CEA26B]/40 bg-[#CEA26B]/10 px-4 py-2" data-testid="gala-badge">
          <Wine className="h-4 w-4 text-[#CEA26B]" />
          <span className="text-sm font-semibold text-[#CEA26B] tracking-wide">{g.badge}</span>
        </div>
        {(g.hosts || []).map((h) => (
          <div key={h.name} className="inline-flex items-center gap-2.5 border border-[#CEA26B]/40 bg-[#CEA26B]/10 py-1 pl-1 pr-4" data-testid={`gala-host-${h.name.replace(/\s+/g, "-").toLowerCase()}`}>
            <img src={speakerSrc(h.photo, 160)} alt={h.name} className="h-9 w-9 object-cover object-top border border-[#CEA26B]/40" />
            <span className="text-sm text-white font-semibold">{h.name}</span>
            <span className="text-sm text-[#CEA26B]">· {h.label}</span>
          </div>
        ))}
      </div>
    </Reveal>

    <div className="space-y-5 max-w-3xl">
      <Reveal>
        <p className="text-lg md:text-xl text-white font-display leading-relaxed" data-testid="gala-lead">{g.lead}</p>
      </Reveal>
      {g.paragraphs.map((par, i) => (
        <Reveal key={i} delay={0.05 * (i + 1)}>
          <p className="text-base md:text-lg text-slate-300 leading-relaxed">{par}</p>
        </Reveal>
      ))}
    </div>

    <div>
      <Reveal>
        <h3 className="font-display font-bold text-white text-2xl sm:text-3xl tracking-tight">{g.awardsTitle}</h3>
        <p className="mt-4 text-base text-slate-400 leading-relaxed max-w-3xl">{g.awardsIntro}</p>
      </Reveal>
      <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
        {g.awards.map((a, i) => {
          const Icon = galaAwardIcons[i] || Award;
          return (
            <Reveal key={i} delay={0.06 * i} className="bg-[#010E3A]">
              <div className="p-7 h-full hover:bg-[#CEA26B]/[0.06] transition-colors" data-testid={`gala-award-${i}`}>
                <Icon className="h-7 w-7 text-[#CEA26B]" />
                <h4 className="mt-5 font-display font-bold text-white text-base leading-snug">{a}</h4>
              </div>
            </Reveal>
          );
        })}
      </div>
    </div>

    <Reveal>
      <p className="text-base md:text-lg text-slate-300 leading-relaxed max-w-3xl" data-testid="gala-after">{g.after}</p>
    </Reveal>

    {g.gallery && <GalaGallery gal={g.gallery} />}

    <Reveal>
      <div className="border border-[#CEA26B]/30 bg-[#CEA26B]/[0.06] p-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6" data-testid="gala-more">
        <div>
          <h4 className="font-display font-bold text-white text-xl">{g.moreTitle}</h4>
          <p className="mt-2 text-base text-slate-300">
            {g.moreText}{" "}
            <a
              href={g.moreUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="gala-more-link"
              className="text-[#CEA26B] font-semibold underline underline-offset-2 hover:text-white transition-colors"
            >
              {g.moreLinkLabel}
            </a>
            .
          </p>
        </div>
        <Trophy className="h-10 w-10 text-[#CEA26B] shrink-0" />
      </div>
    </Reveal>

    <Reveal>
      <p className="border-l-2 border-[#CEA26B] pl-5 text-lg md:text-xl text-white font-display leading-relaxed max-w-3xl" data-testid="gala-closing">
        {g.closing}
      </p>
    </Reveal>
  </div>
);

const CaseStudies = ({ c }) => (
  <div className="space-y-16" data-testid="case-studies-content">
    <Reveal>
      <div className="relative overflow-hidden border border-[#CEA26B]/40 bg-[#CEA26B]/[0.08] px-6 py-8 sm:px-9 sm:py-10 max-w-5xl" data-testid="case-studies-selection-status">
        <Award className="absolute -right-5 -bottom-8 h-40 w-40 text-[#CEA26B]/10" aria-hidden="true" />
        <div className="relative max-w-3xl">
          <span className="inline-flex items-center gap-2 bg-[#CEA26B] px-3 py-1 text-xs font-bold uppercase tracking-[0.16em] text-[#01154F]" data-testid="case-studies-selection-badge">
            <Check className="h-3.5 w-3.5" />
            {c.selectionBadge}
          </span>
          <p className="mt-6 text-lg md:text-xl text-white font-display leading-relaxed border-l-2 border-[#CEA26B] pl-5" data-testid="case-studies-selection-lead">{c.intro1}</p>
          <p className="mt-5 text-base md:text-lg text-slate-300 leading-relaxed">{c.intro2}</p>
        </div>
      </div>
    </Reveal>

    <div>
      <Reveal>
        <h3 className="font-display font-bold text-white text-2xl sm:text-3xl tracking-tight" data-testid="case-studies-selected-title">{c.selectedTitle}</h3>
        <p className="mt-4 text-base text-slate-400 leading-relaxed max-w-3xl">{c.selectedIntro}</p>
      </Reveal>
      <div className="mt-8 space-y-6">
        {c.studies.map((study, i) => (
          <Reveal key={study.category} delay={0.06 * i}>
            {study.image ? (
              <article className="group grid min-w-0 max-w-full grid-cols-1 lg:grid-cols-[1.08fr_0.92fr] overflow-hidden border border-white/10 bg-white/[0.02] hover:border-[#CEA26B]/50 transition-colors duration-300" data-testid={`case-study-card-${study.category.toLowerCase()}`}>
                <div className="relative min-w-0 max-w-full aspect-[4/3] lg:aspect-auto min-h-[300px] overflow-hidden bg-white" style={study.imageBackground ? { backgroundColor: study.imageBackground } : undefined}>
                  <img src={study.image} alt={study.imageAlt} loading="lazy" className={`h-full w-full max-w-full ${study.imageFit === "contain" ? "object-contain" : "object-cover"} group-hover:scale-[1.02] transition-transform duration-500`} data-testid={`case-study-image-${study.category.toLowerCase()}`} />
                  <span className="absolute left-0 top-5 bg-[#CEA26B] px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] text-[#01154F]">{study.status}</span>
                  {study.imageCredit && <span className="absolute bottom-2 right-2 max-w-[calc(100%-1rem)] truncate bg-[#010E3A]/90 px-2 py-1 text-[10px] text-slate-300">{study.imageCredit}</span>}
                </div>
                <div className="flex flex-col justify-center p-7 sm:p-10">
                  <span className="overline text-[#CEA26B]">{study.category}</span>
                  <h4 className="mt-3 font-display font-bold text-white text-2xl sm:text-3xl leading-tight" data-testid={`case-study-title-${study.category.toLowerCase()}`}>{study.title}</h4>
                  <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4 border-y border-white/10 py-4">
                    <div><p className="text-xs uppercase tracking-widest text-slate-500">{study.supplierLabel}</p><p className="mt-1 text-sm font-semibold text-white">{study.supplier}</p></div>
                    <div><p className="text-xs uppercase tracking-widest text-slate-500">{study.customerLabel}</p><p className="mt-1 text-sm font-semibold text-white">{study.customer}</p></div>
                  </div>
                  {study.presenters?.length > 0 && (
                    <div className="mt-5" data-testid={`case-study-presenters-${i}`}>
                      <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#CEA26B]" data-testid={`case-study-presenters-label-${i}`}>
                        {study.presentersLabel}
                      </p>
                      <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                        {study.presenters.map((presenter, presenterIndex) => (
                          <div
                            key={presenter.name}
                            className="flex min-w-0 items-center gap-3 sm:flex-1"
                            data-testid={`case-study-presenter-${i}-${presenterIndex}`}
                          >
                            <img
                              src={speakerSrc(presenter.photo, 160)}
                              alt={presenter.name}
                              className="h-14 w-14 shrink-0 border border-[#CEA26B]/40 object-cover object-top"
                              data-testid={`case-study-presenter-photo-${i}-${presenterIndex}`}
                            />
                            <div className="min-w-0">
                              <p className="font-display text-sm font-bold leading-snug text-white" data-testid={`case-study-presenter-name-${i}-${presenterIndex}`}>{presenter.name}</p>
                              <p className="mt-0.5 text-xs leading-snug text-slate-400" data-testid={`case-study-presenter-role-${i}-${presenterIndex}`}>{presenter.role} · {presenter.org}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <p className="mt-6 text-base font-display text-white leading-relaxed">{study.lead}</p>
                  <p className="mt-3 text-sm text-slate-400 leading-relaxed">{study.text}</p>
                  <p className="mt-6 text-xs font-bold uppercase tracking-[0.16em] text-[#CEA26B]">{study.highlightsTitle}</p>
                  <ul className="mt-3 space-y-2.5">
                    {study.highlights.map((highlight, hi) => (
                      <li key={hi} className="flex items-start gap-3 text-sm text-slate-300 leading-relaxed">
                        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#CEA26B]" />
                        {highlight}
                      </li>
                    ))}
                  </ul>
                  <a href={study.url} target="_blank" rel="noopener noreferrer" data-testid={`case-study-source-${study.category.toLowerCase()}`} className="mt-7 inline-flex w-fit items-center gap-2 text-sm font-semibold uppercase tracking-wide text-[#CEA26B] hover:text-white transition-colors">
                    {study.linkLabel}<ArrowRight className="h-4 w-4" />
                  </a>
                </div>
              </article>
            ) : (
              <article className="relative overflow-hidden border border-dashed border-white/20 bg-white/[0.015] px-7 py-8 sm:px-10" data-testid={`case-study-card-${study.category.toLowerCase()}`}>
                <Sparkles className="absolute -right-4 -bottom-5 h-28 w-28 text-white/[0.04]" aria-hidden="true" />
                <div className="relative max-w-3xl">
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="overline text-[#CEA26B]">{study.category}</span>
                    <span className="border border-white/15 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">{study.status}</span>
                  </div>
                  <h4 className="mt-4 font-display font-bold text-white text-xl sm:text-2xl">{study.title}</h4>
                  <p className="mt-3 text-base text-slate-400 leading-relaxed">{study.text}</p>
                </div>
              </article>
            )}
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.1}>
        <p className="mt-7 border-l-2 border-[#CEA26B] pl-5 text-base md:text-lg text-white font-display leading-relaxed max-w-3xl" data-testid="case-studies-present-note">{c.presentNote}</p>
      </Reveal>
    </div>

    {c.juryList && (
      <Reveal>
        <h3 className="font-display font-bold text-white text-2xl sm:text-3xl tracking-tight" data-testid="case-studies-jury-title">{c.juryTitle}</h3>
        <p className="mt-4 text-base text-slate-400 leading-relaxed max-w-3xl">{c.juryIntro}</p>
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10 max-w-5xl">
          {[...c.juryList]
            .sort((a, b) => {
              if (a.chair !== b.chair) return a.chair ? -1 : 1;
              const la = a.name.trim().split(" ").pop();
              const lb = b.name.trim().split(" ").pop();
              return la.localeCompare(lb, "cs");
            })
            .map((j, i) => (
            <div key={j.name} className="flex items-center gap-4 bg-[#010E3A] px-5 py-4" data-testid={`case-studies-juror-${i}`}>
              {j.photo ? (
                <img src={speakerSrc(j.photo, 160)} alt={j.name} className="h-14 w-14 shrink-0 object-cover object-top border border-[#CEA26B]/40" />
              ) : (
                <span className="flex h-14 w-14 shrink-0 items-center justify-center border border-[#CEA26B]/40 bg-[#CEA26B]/10 font-display font-bold text-[#CEA26B] text-base">
                  {j.name.split(" ").map((w) => w[0]).join("")}
                </span>
              )}
              <div className="min-w-0">
                <p className="font-display font-bold text-white text-base leading-snug">{j.name}</p>
                {j.chair && <p className="text-xs uppercase tracking-widest text-[#CEA26B] mt-0.5">{c.juryChairLabel}</p>}
                {(j.role || j.org) && (
                  <p className="text-xs text-slate-400 mt-0.5 leading-snug">
                    {j.role}{j.role && j.org ? " · " : ""}{j.org}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
        <a
          href={c.juryUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="case-studies-jury-link"
          className="mt-6 inline-flex items-center gap-2 text-sm font-semibold tracking-wide uppercase text-[#CEA26B] hover:text-white transition-colors"
        >
          {c.juryLinkLabel}
          <ArrowRight className="h-4 w-4" />
        </a>
      </Reveal>
    )}

  </div>
);

export const Program = () => {
  const { t } = useLanguage();
  const p = t.program;
  const pt = p.pageTabs;
  const { pathname } = useLocation();
  const [params, setParams] = useSearchParams();

  const valid = ["summit", "speakers", "roundtable", "gala", "case-studies"];
  const fromUrl = params.get("tab");
  const isGalaPath = pathname.includes("galavecer") || pathname.endsWith("/gala");
  const isCaseStudiesPath = pathname.includes("pripadove-studie") || pathname.includes("case-studies");
  const initial = !fromUrl && isGalaPath ? "gala" : !fromUrl && isCaseStudiesPath ? "case-studies" : fromUrl;
  const tab = valid.includes(initial) ? initial : "summit";
  const onSpecialPath = isGalaPath || isCaseStudiesPath;
  const setTab = (k) => setParams(k === "summit" && !onSpecialPath ? {} : { tab: k }, { replace: true });

  usePageMeta(
    isCaseStudiesPath
      ? {
          title: `${p.caseStudies.title} | OBAL ROKU SUMMIT 2026`,
          description: `${p.caseStudies.subtitle}. ${p.caseStudies.intro1}`,
          image: "/og-pripadove-studie-2026.jpg",
          path: pathname,
        }
      : {
          title: `${p.pageTabs.summit} | OBAL ROKU SUMMIT 2026`,
          description: `${p.title} — ${p.subtitle}`,
          image: "/og-program-2026.jpg",
          path: pathname,
        }
  );

  const tabs = [
    { key: "summit", label: pt.summit },
    { key: "speakers", label: pt.speakers },
    { key: "roundtable", label: pt.roundtable },
    { key: "gala", label: pt.gala },
    { key: "case-studies", label: pt.caseStudies },
  ];

  const overlines = {
    summit: p.overline,
    speakers: p.overline,
    roundtable: p.roundtable.overline,
    gala: pt.gala,
    "case-studies": p.caseStudies.overline,
  };
  const titles = {
    summit: p.title,
    speakers: p.speakers.title,
    roundtable: p.roundtable.title,
    gala: p.gala.title,
    "case-studies": p.caseStudies.title,
  };

  return (
    <section id="program" className="relative py-24 md:py-32 bg-[#010E3A] border-y border-white/5">
      <div className="container-page">
        <Reveal className="max-w-3xl">
          <span className="overline text-[#CEA26B]">{overlines[tab]}</span>
          <h2 className="mt-4 font-display font-bold tracking-tight text-white text-3xl sm:text-4xl lg:text-5xl leading-tight">
            {titles[tab]}
          </h2>
        </Reveal>

        <div className="mt-8 inline-flex flex-wrap border border-white/15 p-1 gap-1" data-testid="program-subtabs">
          {tabs.map((tb) => (
            <button
              key={tb.key}
              onClick={() => setTab(tb.key)}
              data-testid={`program-subtab-${tb.key}`}
              className={`px-5 py-2.5 text-sm font-semibold tracking-wide transition-colors ${
                tab === tb.key ? "bg-[#CEA26B] text-[#01154F]" : "text-slate-300 hover:text-white"
              }`}
            >
              {tb.label}
            </button>
          ))}
        </div>

        <div className="mt-12">
          {tab === "summit" && <Summit p={p} />}
          {tab === "speakers" && <Speakers s={p.speakers} />}
          {tab === "roundtable" && <Roundtable r={p.roundtable} speakers={p.speakers} />}
          {tab === "gala" && <Gala g={p.gala} />}
          {tab === "case-studies" && <CaseStudies c={p.caseStudies} />}
        </div>
      </div>
    </section>
  );
};
